import React, { ReactNode } from 'react'
import Link from 'next/link'
import classNames from 'classnames'

import Loading from './Loading'
import Icon, { IconType } from './Icon'
import { iconSize } from './Button'

interface Props {
  to: string
  external?: boolean
  newTab?: boolean
  children?: ReactNode

  className?: string
  theme?: 'fill' | 'outline' | 'text' | 'iconOnly'
  size?: 'small' | 'medium' | 'large'
  color?: 'green' | 'black' | 'blue' | 'red'
  fullWidth?: boolean

  iconType?: IconType
  title?: string

  isLoading?: boolean
  disabled?: boolean
  testId?: string
}

export default function LinkButton({
  to,
  external,
  newTab,
  children,
  className,
  theme = 'fill',
  size = 'large',
  color = 'green',
  fullWidth,
  iconType,
  title,
  isLoading = false,
  disabled,
  testId,
}: Props) {
  const tabProps = newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {}
  const buttonClassNames = classNames('Button', className, `Button--${size}`, `Button--${theme}`, {
    disabled,
    fullWidth,
    isLoading,
    [color]: theme === 'fill' || theme === 'text',
  })

  if (isLoading) {
    return (
      <a className={buttonClassNames} tabIndex={-1} data-testid={testId}>
        <Loading isVisible={isLoading} size={theme === 'text' ? iconSize['small'] : iconSize[size]} />
      </a>
    )
  }

  if (external || disabled) {
    return (
      <a
        className={buttonClassNames}
        href={disabled ? undefined : to}
        tabIndex={disabled ? -1 : 0}
        data-testid={testId}
        {...tabProps}
      >
        {iconType && (
          <Icon
            className={classNames('Button__icon', { withText: theme !== 'iconOnly' })}
            name={iconType}
            title={theme === 'iconOnly' ? title || iconType : undefined}
            size={theme === 'iconOnly' && size !== 'small' ? 40 : 24}
          />
        )}
        {theme !== 'iconOnly' && (
          <span role="button" className="Button__text">
            {children || title}
          </span>
        )}
      </a>
    )
  }

  return (
    <Link href={to} legacyBehavior>
      <a className={buttonClassNames} data-testid={testId} {...tabProps} tabIndex={0}>
        {iconType && (
          <Icon
            className={classNames('Button__icon', { withText: theme !== 'iconOnly' })}
            name={iconType}
            title={theme === 'iconOnly' ? title || iconType : undefined}
            size={theme === 'iconOnly' && size !== 'small' ? 40 : 24}
          />
        )}
        {theme !== 'iconOnly' && (
          <span role="button" className="Button__text">
            {children || title}
          </span>
        )}
      </a>
    </Link>
  )
}
